<template>
  <div class="inventory_center-main inventory-center--glass">
    <InventoryHeader
      :inventory-filter="inventoryFilter"
      @filter-changed="handleFilterChanged"
    />
    <div class="inventory_center-content">
      <InventorySideMenu
        :inventory-items="inventoryItems"
        :selected-item="selectedItem"
        :inventory-filter="inventoryFilter"
        :search-query="searchQuery"
        :loading="loading"
        @item-selected="handleItemSelected"
        @search-changed="handleSearchChanged"
      />
      <InventoryPreviewList
        :selected-item="selectedItem"
        :transactions="selectedItemTransactions"
        :loading-transactions="loadingTransactions"
        :current-page="currentPage"
        :items-per-page="itemsPerPage"
        :current-view-filter="transactionTypeFilter"
        :date-filter="dateFilter"
        @page-change="handlePageChange"
        @items-per-page-change="handleItemsPerPageChange"
        @view-filter-changed="handleViewFilterChanged"
        @date-filter-changed="handleDateFilterChanged"
      />
    </div>
  </div>
</template>

<script>
import '../styles/InventoryCenter.css';
import '../styles/inventory-center-glass.css';
import InventoryHeader from '../components/InventoryHeader.vue';
import InventorySideMenu from '../components/InventorySideMenu.vue';
import InventoryPreviewList from '../components/InventoryPreviewList.vue';
import logger from '@/utils/logger';

export default {
  name: 'InventoryCenter',
  components: { InventoryHeader, InventorySideMenu, InventoryPreviewList },
  data() {
    return {
      inventoryItems: [],
      selectedItemTransactions: [],
      selectedItem: null,
      inventoryFilter: 'active',
      dateFilter: 'all',
      transactionTypeFilter: 'all',
      currentPage: 1,
      itemsPerPage: 10,
      searchQuery: '',
      loading: false,
      loadingTransactions: false,
      inventoryOnHandListener: null,
    };
  },
  computed: {
    companyId() {
      return this.$route.params.id;
    },
  },
  watch: {
    companyId: {
      immediate: false,
      handler() {
        this.fetchInventoryItems();
      },
    },
  },
  methods: {
    mapInventoryItem(item) {
      const onHand = item.onHandQuantity != null ? Number(item.onHandQuantity) : 0;
      const initialQty =
        item.initialQuantity != null && item.initialQuantity !== ''
          ? Number(item.initialQuantity)
          : onHand;
      const toPriceOrNull = (value) =>
        value != null && value !== '' && Number.isFinite(Number(value)) ? Number(value) : null;
      const salesPrice = item.sellProduct === false ? null : toPriceOrNull(item.salesPrice);
      const purchasePrice = toPriceOrNull(item.purchasePrice);
      const standardCost = toPriceOrNull(item.standardCost);
      const asOfRaw = item.asOfDate || item.createdAt || null;
      const asOfDate = asOfRaw
        ? new Date(asOfRaw).toISOString().slice(0, 10)
        : '';
      return {
        id: item.id,
        name: item.name || '',
        qty: onHand,
        onHandQuantity: onHand,
        initialQuantity: Number.isFinite(initialQty) ? initialQty : 0,
        asOfDate,
        salesPrice,
        purchasePrice,
        standardCost,
        group: item.itemGroup?.name || item.group || '—',
        warehouse: item.warehouse || '—',
        vendorProduct: item.vendorProductNo || item.vendorProduct || '—',
        no: item.vendorProductNo || `INV-${item.id}`,
        status: item.active === false || item.active === 0 || item.active === '0' ? 'inactive' : 'active',
        reorderPoint: item.reorderPoint != null ? Number(item.reorderPoint) : null,
      };
    },
    async fetchAssemblies(companyId) {
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/assemblies`
        );
        if (!response.ok) return [];
        const data = await response.json();
        return Array.isArray(data) ? data : data?.data || [];
      } catch (error) {
        logger.error('Error loading assemblies for inventory centre:', error);
        return [];
      }
    },
    buildItemRoleLookup(assemblies, rows) {
      const normalize = (name) => String(name || '').trim().toLowerCase();
      const unitCostById = new Map();
      for (const row of rows) {
        const cost = Number(row.purchasePrice ?? row.standardCost);
        if (Number.isFinite(cost)) unitCostById.set(Number(row.id), cost);
      }

      const costFromPartsByName = new Map();
      const usedInById = new Map();
      for (const assembly of assemblies) {
        let costFromParts = 0;
        for (const component of assembly.items || []) {
          const id = Number(component.inventoryItemId);
          const perUnitQty = Number(component.quantity) || 0;
          const unitCost = unitCostById.has(id) ? unitCostById.get(id) : Number(component.avgCost) || 0;
          costFromParts += perUnitQty * unitCost;
          if (!Number.isFinite(id)) continue;
          if (!usedInById.has(id)) usedInById.set(id, []);
          usedInById.get(id).push(assembly.name);
        }
        costFromPartsByName.set(normalize(assembly.name), Math.round(costFromParts * 100) / 100);
      }
      return { normalize, costFromPartsByName, usedInById };
    },
    // Build Assembly creates an inventory item with the assembly's name for finished goods.
    resolveItemRole(item, { normalize, costFromPartsByName, usedInById }) {
      const usedIn = usedInById.get(Number(item.id)) || [];
      const assemblyKey = normalize(item.name);
      if (costFromPartsByName.has(assemblyKey)) {
        return {
          role: 'finished',
          roleLabel: 'Finished good',
          roleIcon: 'bi bi-box-seam-fill',
          usedIn: [],
          costFromParts: costFromPartsByName.get(assemblyKey),
          // Finished goods are built, not bought; the copied value is the assembly's standard cost.
          purchasePrice: null,
        };
      }
      if (usedIn.length || item.sellProduct === false) {
        return { role: 'part', roleLabel: 'Component', roleIcon: 'bi bi-puzzle', usedIn };
      }
      return { role: 'resale', roleLabel: 'Resale item', roleIcon: 'bi bi-arrow-left-right', usedIn: [] };
    },
    async fetchInventoryItems({ keepSelection = true } = {}) {
      const companyId = this.companyId;
      if (!companyId) return;

      const previousSelectedId = keepSelection ? this.selectedItem?.id : null;
      this.loading = true;
      try {
        const [response, assemblies] = await Promise.all([
          fetch(`${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/items`),
          this.fetchAssemblies(companyId),
        ]);
        if (!response.ok) throw new Error('Failed to fetch inventory items');
        const data = await response.json();
        const rows = Array.isArray(data) ? data : data?.data || data?.items || [];
        const roles = this.buildItemRoleLookup(assemblies, rows);
        this.inventoryItems = rows.map((item) => ({
          ...this.mapInventoryItem(item),
          ...this.resolveItemRole(item, roles),
        }));

        let nextSelected = null;
        if (previousSelectedId != null) {
          nextSelected = this.inventoryItems.find(
            (item) => Number(item.id) === Number(previousSelectedId)
          );
        }
        if (!nextSelected) {
          nextSelected =
            this.inventoryItems.find((item) => item.status === this.inventoryFilter) ||
            this.inventoryItems[0] ||
            null;
        }
        this.selectedItem = nextSelected;
        if (nextSelected) {
          await this.fetchItemTransactions(nextSelected.id);
        } else {
          this.selectedItemTransactions = [];
        }
      } catch (error) {
        logger.error('Error loading inventory centre items:', error);
        this.inventoryItems = [];
        this.selectedItem = null;
        this.selectedItemTransactions = [];
      } finally {
        this.loading = false;
      }
    },
    async fetchItemTransactions(itemId) {
      const companyId = this.companyId;
      if (!companyId || !itemId) {
        this.selectedItemTransactions = [];
        return;
      }
      this.loadingTransactions = true;
      try {
        const response = await fetch(
          `${process.env.VUE_APP_API_URL}/inventory/companies/${companyId}/items/${itemId}/transactions`
        );
        if (!response.ok) throw new Error('Failed to fetch inventory transactions');
        const data = await response.json();
        const txns = Array.isArray(data?.transactions)
          ? data.transactions
          : Array.isArray(data?.quantityHistory)
            ? data.quantityHistory
            : [];
        this.selectedItemTransactions = txns.map((txn) => ({
          ...txn,
          qty: Number(txn.qty) || 0,
          amount: Number(txn.amount) || 0,
          warehouseName: txn.warehouseName || '',
          balanceAfter:
            txn.balanceAfter != null && txn.balanceAfter !== ''
              ? Number(txn.balanceAfter)
              : null,
        }));

        if (this.selectedItem) {
          const onHand =
            data?.onHandQuantity != null
              ? Number(data.onHandQuantity) || 0
              : this.selectedItem.onHandQuantity;
          const initialQuantity =
            data?.initialQuantity != null
              ? Number(data.initialQuantity) || 0
              : this.selectedItem.initialQuantity;
          const asOfDate = data?.asOfDate || this.selectedItem.asOfDate || '';
          const warehouseNames = [];
          for (const txn of this.selectedItemTransactions) {
            const name = String(txn.warehouseName || '').trim();
            if (name && !warehouseNames.includes(name)) warehouseNames.push(name);
          }
          const warehouse = warehouseNames.length
            ? warehouseNames.join(', ')
            : this.selectedItem.warehouse || '—';
          this.selectedItem = {
            ...this.selectedItem,
            qty: onHand,
            onHandQuantity: onHand,
            initialQuantity,
            asOfDate,
            warehouse,
          };
          const idx = this.inventoryItems.findIndex(
            (item) => Number(item.id) === Number(this.selectedItem.id)
          );
          if (idx >= 0) {
            this.inventoryItems.splice(idx, 1, {
              ...this.inventoryItems[idx],
              qty: onHand,
              onHandQuantity: onHand,
              initialQuantity,
              asOfDate,
              warehouse: this.selectedItem.warehouse,
            });
          }
        }
      } catch (error) {
        logger.error('Error loading inventory transactions:', error);
        this.selectedItemTransactions = [];
      } finally {
        this.loadingTransactions = false;
      }
    },
    handleFilterChanged(newFilter) {
      this.inventoryFilter = newFilter;
      if (
        this.selectedItem &&
        this.selectedItem.status !== newFilter &&
        newFilter !== 'all'
      ) {
        const first = this.inventoryItems.find((item) => item.status === newFilter);
        this.selectedItem = first || null;
        if (first) this.fetchItemTransactions(first.id);
        else this.selectedItemTransactions = [];
      }
    },
    handleItemSelected(itemId) {
      const item = this.inventoryItems.find((i) => Number(i.id) === Number(itemId));
      if (item) {
        this.selectedItem = item;
        this.currentPage = 1;
        this.fetchItemTransactions(item.id);
      }
    },
    handleSearchChanged(searchQuery) {
      this.searchQuery = searchQuery;
    },
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleItemsPerPageChange(itemsPerPage) {
      this.itemsPerPage = itemsPerPage;
      this.currentPage = 1;
    },
    handleViewFilterChanged(viewFilter) {
      this.transactionTypeFilter = viewFilter;
      this.currentPage = 1;
    },
    handleDateFilterChanged(dateFilter) {
      this.dateFilter = dateFilter;
      this.currentPage = 1;
    },
    setupInventoryRealtime() {
      if (this.inventoryOnHandListener) return;
      this.inventoryOnHandListener = (event) => this.handleInventoryOnHandUpdated(event);
      window.addEventListener('inventory-on-hand-updated', this.inventoryOnHandListener);
      import('@/services/companyPresenceClient')
        .then((m) => m.connectCompanyPresence())
        .catch(() => {});
    },
    teardownInventoryRealtime() {
      if (typeof this.inventoryOnHandListener === 'function') {
        window.removeEventListener('inventory-on-hand-updated', this.inventoryOnHandListener);
        this.inventoryOnHandListener = null;
      }
    },
    handleInventoryOnHandUpdated(event) {
      const detail = event?.detail || {};
      const eventCompanyId = Number(detail.companyId);
      const routeCompanyId = Number(this.companyId);
      if (
        Number.isFinite(eventCompanyId) &&
        Number.isFinite(routeCompanyId) &&
        eventCompanyId !== routeCompanyId
      ) {
        return;
      }

      const updates = Array.isArray(detail.items) ? detail.items : [];
      if (!updates.length) {
        this.fetchInventoryItems();
        return;
      }

      const byId = new Map(
        updates
          .map((row) => [Number(row.id), row])
          .filter(([id]) => Number.isFinite(id))
      );

      this.inventoryItems = this.inventoryItems.map((item) => {
        const update = byId.get(Number(item.id));
        if (!update) return item;
        const onHand = Number(update.onHandQuantity) || 0;
        return { ...item, qty: onHand, onHandQuantity: onHand };
      });

      if (this.selectedItem && byId.has(Number(this.selectedItem.id))) {
        const update = byId.get(Number(this.selectedItem.id));
        const onHand = Number(update.onHandQuantity) || 0;
        this.selectedItem = {
          ...this.selectedItem,
          qty: onHand,
          onHandQuantity: onHand,
        };
        // Refresh transaction list so the new invoice/sale appears
        this.fetchItemTransactions(this.selectedItem.id);
      }
    },
  },
  mounted() {
    this.fetchInventoryItems();
    this.setupInventoryRealtime();
  },
  beforeUnmount() {
    this.teardownInventoryRealtime();
  },
};
</script>
